<template>
  <SideMenu
    ref="sideMenu"
    :heading="$lang('checkout', 'added_to_shopping_cart_header')"
    :side="'right'"
    @on-side-menu-close="cartStore.showAddedItem = false"
  >
    <div v-if="item">
      <div class="flex items-center p-24 border-b border-b-black-25">
        <div
          class="
            w-80
            h-96
            flex
            justify-center
            items-center
            rounded-regular
            overflow-hidden
          "
        >
          <img
            v-if="isGiftCard"
            src="~/assets/images/ullmax_giftcard.jpg"
            class="w-full h-full object-cover"
            :alt="$lang('product_page', 'product_image_alt')"
            height="94"
            width="80"
          >
          <nuxt-img
            v-else
            :src="item.standardImage"
            preset="standard"
            sizes="mob414:80px desk:80px"
            class="w-full h-full object-cover"
            :alt="$lang('product_page', 'product_image_alt')"
          />
        </div>
        <div class="pl-16 flex-1">
          <div class="text-product-name mb-4">
            {{ item.title }}
          </div>
          <div v-if="item.showAsOnSale" class="text-price mb-4">
            <span class="text-warning mr-2 sm:mr-8">
              {{ item.price }}
            </span>
            <span class="line-through text-muted">
              {{ item.priceBeforeDiscount }}
            </span>
          </div>
          <div v-else class="text-price mb-4">
            <span>
              {{ item.price }}
            </span>
          </div>
          <div class="text-size-chart">
            {{ itemVersion }}
          </div>
        </div>
      </div>
      <div v-if="!isGiftCard" class="p-24 border-b border-b-black-25">
        <div class="text-small-medium mb-12">
          {{ config.cart_cross_sell.title }}
        </div>
        <div
          class="flex"
          :class="{
            'justify-between': crossSellProducts.length > 2,
          }"
        >
          <UpsellItem
            v-for="cartUpsellProduct in crossSellProducts"
            :key="'item-added-upsell-product' + cartUpsellProduct.ecomId"
            :item="cartUpsellProduct"
          />
        </div>
      </div>
      <div class="p-24">
        <ButtonComponent
          v-if="!isGiftCard"
          class="mb-12"
          :text="$lang('checkout', 'back_to_shopping_btn')"
          is-secondary-btn
          is-large-btn
          is-block
          @clicked="cartStore.showAddedItem = false"
        />
        <ButtonComponent
          :text="$lang('checkout', 'checkout_btn')"
          :link-url="config.checkoutRoute"
          is-large-btn
          is-block
        />
      </div>
    </div>
  </SideMenu>
</template>

<script setup lang="ts">
import SideMenu from '~/components/navigation/SideMenu.vue';
import { useCartStore } from '~/store/cart';
import { useConfigStore } from '~/store/config';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import type { CartItemType } from '~/constants/types/cart-item';
import Product from '~/models/product';
import type { EcomProduct } from '~/constants/types/product';
import UpsellItem from '~/components/cart/UpsellItem.vue';
const cartStore = useCartStore();
const config = useConfigStore();
const { fetchProducts } = useProductService();
const sideMenu = ref<typeof SideMenu | null>(null);
const crossSellProducts = ref([]);

onMounted(()=> {
  getCrossSellProducts();
  if (sideMenu.value) {
    sideMenu.value.openOverlay();
  }
});

const item = computed(()=> {
  return cartStore.addedItem?.product;
});

const isGiftCard = computed(()=> {
  return item.value?.isGiftCard === true;
});

const itemVersion = computed(()=> {
  if (item.value) {
    // Check if it's a gift card (won't have items array)
    if (!item.value.items) {
      return '';
    }
    
    const color = item.value.color?.desc?.trim();
    const version = item.value.items.find((item) => item.item === cartStore.addedItem?.itemId);

    if (version && !item.value.hideSizeChoice) {
      if (!item.value.hideColorChoice) {
        return `${color}, ${version.name}`;
      } else {
        return `${version.name}`;
      }
    } else if (!item.value.hideColorChoice) {
      return color;
    } else {
      return '';
    }
  }
  return '';
});

const getCrossSellProducts= async() =>  {
  if (item.value) {
    // Check if item has ecomId (regular product) - gift cards won't have this
    const filterItems = item.value.ecomId ? [ item.value.ecomId ] : [];

    cartStore.cartData.items.forEach((product: CartItemType) => {
      filterItems.push(
        new Product({ ecomData: product.product, contentData: null }).ecomId
      );
    });

    if (config.cart_cross_sell?.products.length > 0) {
      const { products } = await fetchProducts({
        products: config.cart_cross_sell.products,
      });

      crossSellProducts.value = products
        .map(
          (product: EcomProduct) =>
            new Product({ ecomData: product, contentData: null })
        )
        .filter((product: Product) => !filterItems.includes(product.ecomId))
        .slice(0, 3);
    }
  }
};

</script>

<style scoped lang="postcss">
</style>
